import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import '../../quiz-bank/tracking/events.quiz-bank';
import { useImpressionRef } from '@zep/logger/react';
import { trackEvent } from '@zep/logger/tracking';
import { ChevronRightIcon } from '@/icons';
import { buttonVariants } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { type BuildType } from '@/views/build/context/BuildInfoProvider';
export type QuizCreateMethod = 'DIRECT' | 'BANK';
function QuizCreateMethodCard({
  image,
  label,
  title,
  onClick
}: {
  image: string;
  label: string;
  title: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="group relative flex min-h-[320px] flex-1 cursor-pointer flex-col items-center justify-center rounded-lg border border-opacity-assistive bg-gray-100 px-xl pb-lg text-center transition hover:bg-gray-200 tablet-sm:min-h-[400px]" data-sentry-component="QuizCreateMethodCard" data-sentry-source-file="QuizCreateMethodView.tsx">
      {/* 장식용 이동 표시. 카드 전체가 버튼이라 중첩 버튼을 피해 span 으로 그린다 */}
      <span aria-hidden className={cn(buttonVariants({
      variant: 'outlined',
      intent: 'neutral',
      size: 'md'
    }), 'pointer-events-none absolute right-[17px] top-[17px] aspect-square rounded-full p-0')}>
        <ChevronRightIcon size="md" fill={ICON_COLOR.neutral} data-sentry-element="ChevronRightIcon" data-sentry-source-file="QuizCreateMethodView.tsx" />
      </span>
      <div className="flex flex-col items-center transition-transform duration-200 ease-out group-hover:-translate-y-1">
        <div className="relative size-[190px] shrink-0 overflow-hidden">
          {/* 일러스트(396px 2x)를 박스보다 살짝 크게 얹어 여백 없이 채운다 */}
          <img src={image} alt="" className="absolute left-[-4px] top-[4px] size-[198px] max-w-none" />
        </div>
        <div className="flex flex-col items-center gap-3xs">
          <p className="text-body-md-semibold text-alternative">{label}</p>
          <p className="text-heading-md-bold text-default">{title}</p>
        </div>
      </div>
    </button>;
}
interface QuizCreateMethodViewProps {
  /** 트래킹 세그먼트 — 신규 생성인지 편집·복제인지 갈라 본다 */
  buildType: BuildType;
  /** 은행 화면에 넘길 7자리 카테고리 트리 id. 과목별 채택률을 가르는 트래킹 키이기도 하다 */
  categoryTreeId: string;
  onSelect: (method: QuizCreateMethod) => void;
}

/** 과목 선택 뒤 "직접 만들기 / 문제 은행" 분기. 문제 은행이 열리는 과목 조합에서만 나타난다. */
export const QuizCreateMethodView = ({
  buildType,
  categoryTreeId,
  onSelect
}: QuizCreateMethodViewProps) => {
  const {
    t
  } = useTranslation(['build']);

  /* 이 화면의 노출이 은행 퍼널의 **분모**다 — 채택률 = method_select(bank) ÷ method_view. */
  const impressionRef = useImpressionRef<HTMLDivElement>(() => trackEvent('quiz_setup_method_view', {
    place: 'setup_modal',
    build_type: buildType,
    category_tree_id: categoryTreeId
  }));
  return <div ref={impressionRef} className="flex min-h-0 flex-1 flex-col gap-md overflow-y-auto px-lg pb-lg tablet-sm:flex-row tablet-sm:items-stretch" data-sentry-component="QuizCreateMethodView" data-sentry-source-file="QuizCreateMethodView.tsx">
      <QuizCreateMethodCard image="/assets/quiz-bank/quiz-create-direct.webp" label={t('build:quizBank.createMethod.direct.label')} title={t('build:quizBank.createMethod.direct.title')} onClick={() => {
      trackEvent('quiz_setup_method_select', {
        place: 'setup_modal',
        build_type: buildType,
        method: 'direct',
        destination: 'build_form',
        category_tree_id: categoryTreeId
      });
      onSelect('DIRECT');
    }} data-sentry-element="QuizCreateMethodCard" data-sentry-source-file="QuizCreateMethodView.tsx" />
      <QuizCreateMethodCard image="/assets/quiz-bank/quiz-create-bank.webp" label={t('build:quizBank.createMethod.bank.label')} title={t('build:quizBank.createMethod.bank.title')} onClick={() => {
      trackEvent('quiz_setup_method_select', {
        place: 'setup_modal',
        build_type: buildType,
        method: 'bank',
        destination: 'quiz_bank',
        category_tree_id: categoryTreeId
      });
      onSelect('BANK');
    }} data-sentry-element="QuizCreateMethodCard" data-sentry-source-file="QuizCreateMethodView.tsx" />
    </div>;
};